/**
 * Browser-side GCS resumable upload using a v4 signed "resumable" init URL from the API.
 * @see https://cloud.google.com/storage/docs/performing-resumable-uploads
 */

import { PERCENT_SCALE } from 'utils/units'

const DEFAULT_CHUNK_SIZE_MEBIBYTES = 8
const BYTES_PER_KIBIBYTE = 1024
const DEFAULT_CHUNK_SIZE_BYTES = DEFAULT_CHUNK_SIZE_MEBIBYTES * BYTES_PER_KIBIBYTE * BYTES_PER_KIBIBYTE
const DEFAULT_UPLOAD_CONTENT_TYPE = 'video/mp4'
const RANGE_COMPONENT_LIMIT = 2
const ERROR_BODY_PREVIEW_LENGTH = 200
const HTTP_STATUS_OK = 200
const HTTP_STATUS_CREATED = 201
const HTTP_STATUS_RESUME_INCOMPLETE = 308
const MAXIMUM_IN_PROGRESS_PERCENTAGE = PERCENT_SCALE - 1

export const parseRangeEndInclusive = (rangeHeader: string | null): number | null => {
  if (!rangeHeader?.startsWith('bytes=')) {
    return null
  }

  const range = rangeHeader.slice('bytes='.length)
  const [start, end] = range.split('-', RANGE_COMPONENT_LIMIT)

  if (!end) {
    return null
  }

  const endN = Number.parseInt(end, 10)
  const startN = Number.parseInt(start, 10)
  if (!Number.isFinite(endN) || !Number.isFinite(startN)) {
    return null
  }

  return endN
}

const initiateGcsResumableUpload = async (
  initSignedUrl: string,
  contentType: string,
  signal?: AbortSignal
): Promise<string> => {
  const response = await fetch(initSignedUrl, {
    method: 'POST',
    headers: {
      'X-Goog-Resumable': 'start',
      'Content-Type': contentType,
    },
    signal,
  })

  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(
      `Failed to start resumable upload (${response.status}): ${text.slice(0, ERROR_BODY_PREVIEW_LENGTH)}`
    )
  }

  const location = response.headers.get('Location')
  if (!location) {
    throw new Error('Resumable upload response missing Location header')
  }

  return location
}

const queryResumableByteOffset = async (
  sessionUrl: string,
  totalSize: number,
  signal?: AbortSignal
): Promise<number> => {
  const contentRange = Number.isFinite(totalSize) && totalSize >= 0 ? `bytes */${totalSize}` : 'bytes */*'
  const response = await fetch(sessionUrl, {
    method: 'PUT',
    headers: {
      'Content-Length': '0',
      'Content-Range': contentRange,
    },
    signal,
  })

  if (response.status === HTTP_STATUS_RESUME_INCOMPLETE) {
    const endInclusive = parseRangeEndInclusive(response.headers.get('Range'))
    if (endInclusive === null) {
      return 0
    }

    return endInclusive + 1
  }

  if (response.status === HTTP_STATUS_OK || response.status === HTTP_STATUS_CREATED) {
    return totalSize
  }

  const text = await response.text().catch(() => '')
  throw new Error(`Resumable status check failed (${response.status}): ${text.slice(0, ERROR_BODY_PREVIEW_LENGTH)}`)
}

const putChunk = async (parameters: {
  sessionUrl: string
  start: number
  endInclusive: number
  total: number
  body: Blob
  signal?: AbortSignal
}): Promise<{ status: 'complete' } | { status: 'continue'; nextOffset: number }> => {
  const { body, endInclusive, sessionUrl, signal, start, total } = parameters
  const response = await fetch(sessionUrl, {
    method: 'PUT',
    headers: {
      'Content-Length': String(body.size),
      'Content-Range': `bytes ${start}-${endInclusive}/${total}`,
    },
    body,
    signal,
  })

  if (response.status === HTTP_STATUS_RESUME_INCOMPLETE) {
    const endReceived = parseRangeEndInclusive(response.headers.get('Range'))
    if (endReceived === null) {
      const nextOffset = await queryResumableByteOffset(sessionUrl, total, signal)
      return { status: 'continue', nextOffset }
    }

    return { status: 'continue', nextOffset: endReceived + 1 }
  }

  if (response.status === HTTP_STATUS_OK || response.status === HTTP_STATUS_CREATED) {
    return { status: 'complete' }
  }

  const text = await response.text().catch(() => '')
  throw new Error(`Chunk upload failed (${response.status}): ${text.slice(0, ERROR_BODY_PREVIEW_LENGTH)}`)
}

export const uploadFileResumable = async (parameters: {
  initSignedUrl: string
  file: File
  chunkSizeBytes?: number
  onProgress?: (percent: number) => void
  signal?: AbortSignal
}): Promise<void> => {
  const { file, initSignedUrl, onProgress, signal } = parameters
  const chunkSizeBytes = parameters.chunkSizeBytes ?? DEFAULT_CHUNK_SIZE_BYTES
  const total = file.size
  const contentType = file.type || DEFAULT_UPLOAD_CONTENT_TYPE

  if (total === 0) {
    const sessionUrl = await initiateGcsResumableUpload(initSignedUrl, contentType, signal)
    const response = await fetch(sessionUrl, {
      method: 'PUT',
      headers: {
        'Content-Length': '0',
        'Content-Range': 'bytes */0',
      },
      signal,
    })
    if (response.status !== HTTP_STATUS_OK && response.status !== HTTP_STATUS_CREATED) {
      const text = await response.text().catch(() => '')
      throw new Error(`Empty object upload failed (${response.status}): ${text.slice(0, ERROR_BODY_PREVIEW_LENGTH)}`)
    }
    onProgress?.(PERCENT_SCALE)
    return
  }

  const sessionUrl = await initiateGcsResumableUpload(initSignedUrl, contentType, signal)

  let offset = 0

  while (offset < total) {
    signal?.throwIfAborted()

    const end = Math.min(offset + chunkSizeBytes, total) - 1
    const chunk = file.slice(offset, end + 1)

    try {
      const outcome = await putChunk({
        sessionUrl,
        start: offset,
        endInclusive: end,
        total,
        body: chunk,
        signal,
      })

      if (outcome.status === 'complete') {
        onProgress?.(PERCENT_SCALE)
        return
      }

      const previousOffset = offset
      offset = outcome.nextOffset
      if (offset <= previousOffset) {
        offset = await queryResumableByteOffset(sessionUrl, total, signal)
      }
      if (offset >= total) {
        onProgress?.(PERCENT_SCALE)
        return
      }
      onProgress?.(Math.min(MAXIMUM_IN_PROGRESS_PERCENTAGE, Math.round((offset / total) * PERCENT_SCALE)))
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }
      const nextOffset = await queryResumableByteOffset(sessionUrl, total, signal)
      if (nextOffset >= total) {
        onProgress?.(PERCENT_SCALE)
        return
      }
      offset = nextOffset
    }
  }

  onProgress?.(PERCENT_SCALE)
}
